import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';

interface DevotionalContent {
  id: string;
  day_number: number;
  step_number: number;
  step_title: string;
  lesson_title: string;
  lesson_content: string;
  scripture_reference: string;
  scripture_text: string;
  prayer: string;
}

export default function DevotionalScreen() {
  const [devotional, setDevotional] = useState<DevotionalContent | null>(null);
  const [serenityPrayer, setSerenityPrayer] = useState<string>('');
  const [twelveSteps, setTwelveSteps] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPrayer, setShowPrayer] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number>(() => {
    const today = new Date();
    return Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);
  });

  useEffect(() => {
    fetchDevotional();
    fetchStaticContent();
  }, [selectedDay]);

  const fetchStaticContent = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('devotional_static_content')
        .select('*')
        .order('display_order');

      if (fetchError) throw fetchError;

      if (data) {
        const prayer = data.find(item => item.content_key === 'serenity_prayer');
        const steps = data.find(item => item.content_key === '12_steps');
        if (prayer) setSerenityPrayer(prayer.content_text);
        if (steps) setTwelveSteps(steps.content_text);
      }
    } catch (err) {
      console.error('Failed to load static content:', err);
    }
  };

  const fetchDevotional = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('devotional_content')
        .select('*')
        .eq('day_number', selectedDay)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setDevotional(data);
      } else {
        setDevotional(null);
        setError('No devotional available for this day.');
      }
    } catch (err) {
      setError('Failed to load devotional');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const goToToday = () => {
    const today = new Date();
    const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);
    setSelectedDay(dayOfYear);
  };

  const previousDay = () => {
    if (selectedDay > 1) {
      setSelectedDay(selectedDay - 1);
    }
  };

  const nextDay = () => {
    if (selectedDay < 365) {
      setSelectedDay(selectedDay + 1);
    }
  };

  const today = new Date();
  const currentDay = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#B85C38] py-12 px-6 text-center">
        <h1 className="text-4xl font-bold text-white mb-3">Daily Devotional</h1>
        <p className="text-lg text-white">Daily readings for recovery and spiritual growth</p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/" className="inline-block text-gray-600 hover:text-gray-900 mb-6">
          ← Back to Home
        </Link>

        {serenityPrayer && (
          <div className="bg-white rounded-xl shadow-md mb-6 overflow-hidden">
            <button
              onClick={() => setShowPrayer(!showPrayer)}
              className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50"
            >
              <h2 className="text-2xl font-bold text-gray-900">The Serenity Prayer</h2>
              <span className="text-2xl text-gray-600">{showPrayer ? '▲' : '▼'}</span>
            </button>
            {showPrayer && (
              <div className="px-6 pb-6">
                <p className="text-gray-700 italic leading-relaxed whitespace-pre-line">{serenityPrayer}</p>
              </div>
            )}
          </div>
        )}

        {twelveSteps && (
          <div className="bg-white rounded-xl shadow-md mb-6 overflow-hidden">
            <button
              onClick={() => setShowSteps(!showSteps)}
              className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50"
            >
              <h2 className="text-2xl font-bold text-gray-900">The 12 Steps of Overcomers</h2>
              <span className="text-2xl text-gray-600">{showSteps ? '▲' : '▼'}</span>
            </button>
            {showSteps && (
              <div className="px-6 pb-6">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{twelveSteps}</p>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md p-8 mb-6">
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <p className="text-lg font-semibold text-gray-600">Day {selectedDay} of the Year</p>
            {selectedDay !== currentDay && (
              <button
                onClick={goToToday}
                className="bg-[#B85C38] text-white px-6 py-2 rounded-lg font-bold hover:bg-[#A34C28]"
              >
                Go to Today
              </button>
            )}
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block h-12 w-12 border-4 border-[#B85C38] border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">Loading devotional...</p>
            </div>
          )}

          {error && !devotional && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-yellow-800">{error}</p>
            </div>
          )}

          {devotional && (
            <div>
              <div className="inline-block bg-[#E8DDD0] px-4 py-2 rounded-full mb-4">
                <span className="text-[#B85C38] font-bold text-sm">Step {devotional.step_number} of 12</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{devotional.lesson_title}</h2>
              <p className="text-gray-600 italic mb-6">{devotional.step_title}</p>

              <p className="text-gray-700 leading-relaxed mb-6 text-lg whitespace-pre-line">{devotional.lesson_content}</p>

              <div className="bg-[#E8DDD0] rounded-lg p-6 border-l-4 border-[#B85C38] mb-6">
                <p className="font-bold text-gray-900 mb-2">{devotional.scripture_reference}</p>
                <p className="text-gray-800 italic leading-relaxed">{devotional.scripture_text}</p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6 border-l-4 border-[#8B7355] mb-6">
                <p className="text-gray-700 font-medium">
                  Take a moment to reflect on this message today. How does it apply to your journey of recovery?
                </p>
              </div>

              <div className="bg-[#E8DDD0] rounded-lg p-6 border-l-4 border-[#8B7355]">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Prayer</h3>
                <p className="text-gray-800 italic leading-relaxed whitespace-pre-line">{devotional.prayer}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center items-center gap-6 mb-6">
          <button
            onClick={previousDay}
            disabled={selectedDay <= 1}
            className="bg-gray-300 text-gray-900 px-8 py-3 rounded-lg font-bold hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>

          <span className="text-gray-600 font-semibold">Day {selectedDay}</span>

          <button
            onClick={nextDay}
            disabled={selectedDay >= 365}
            className="bg-[#B85C38] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#A34C28] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>

        <div className="bg-[#E8DDD0] rounded-xl p-8 border border-[#B85C38]">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Join Our Community</h3>
          <p className="text-gray-700 leading-relaxed mb-4 text-center">
            Join our Overcomers 12-Step group every weekday at 10:00 AM to connect with others on the path to recovery.
            These daily devotionals are designed to support your journey through each step.
          </p>
          <p className="text-gray-600 text-sm text-center">
            Remember: You are not alone in this journey. Reach out to connect with a case manager anytime you need support.
          </p>
        </div>
      </div>
    </div>
  );
}
